// utils/creditCalculations.ts

import { TOTAL_CREDITS_REQUIRED } from './constants';

/**
 * Unified credit calculation utilities
 * Addresses the global vs per-major credit calculation inconsistencies
 */

/**
 * Calculate credits for a major based on its data
 * Handles negative credit edge cases and ensures consistency
 */
interface MajorData {
  credits_completed?: number;
  credits_in_progress?: number;
  credits_planned?: number;
  credits_suggested?: number;
  credits_available?: number;
}

interface CreditBreakdown {
  credits_completed: number;
  credits_in_progress: number;
  credits_planned: number;
  credits_suggested: number;
  credits_available: number;
  credits_total: number;
  completion_percentage: number;
  is_complete: boolean;
  excess_credits: number;
  credits_required?: number;
}

export const calculateMajorCredits = (majorData: MajorData | null | undefined, totalCreditsRequired: number = TOTAL_CREDITS_REQUIRED): CreditBreakdown => {
  if (!majorData) {
    return {
      credits_completed: 0,
      credits_in_progress: 0,
      credits_planned: 0,
      credits_suggested: 0,
      credits_available: 0,
      credits_total: 0,
      completion_percentage: 0,
      is_complete: false,
      excess_credits: 0
    };
  }

  const completed = majorData.credits_completed || 0;
  const inProgress = majorData.credits_in_progress || 0;
  const planned = majorData.credits_planned || 0;
  const suggested = majorData.credits_suggested || 0;

  // Calculate total earned/planned credits (suggested not counted as committed)
  const totalEarned = completed + inProgress + planned;

  // Calculate available credits (ensure never negative)
  const rawAvailable = totalCreditsRequired - totalEarned - suggested;
  const available = Math.max(0, majorData.credits_available || rawAvailable);

  // Calculate completion percentage based on completed credits only
  const completionPercentage = (completed / totalCreditsRequired) * 100;

  // Determine if complete and calculate excess
  const isComplete = totalEarned >= totalCreditsRequired;
  const excessCredits = totalEarned > totalCreditsRequired ? totalEarned - totalCreditsRequired : 0;

  return {
    credits_completed: completed,
    credits_in_progress: inProgress,
    credits_planned: planned,
    credits_suggested: suggested,
    credits_available: available,
    credits_total: totalEarned,
    completion_percentage: Math.round(completionPercentage * 10) / 10,
    is_complete: isComplete,
    excess_credits: excessCredits,
    credits_required: totalCreditsRequired
  };
};

/**
 * Calculate progress bar segments for UI display
 * Returns normalized percentages that sum to 100%
 */
export const calculateProgressBarSegments = (credits: CreditBreakdown, totalCredits: number) => {
  const { credits_completed, credits_in_progress, credits_planned, credits_suggested, credits_available } = credits;

  // Calculate raw percentages
  const completedPct = (credits_completed / totalCredits) * 100;
  const inProgressPct = (credits_in_progress / totalCredits) * 100;
  const plannedPct = (credits_planned / totalCredits) * 100;
  const suggestedPct = ((credits_suggested || 0) / totalCredits) * 100;
  const availablePct = (credits_available / totalCredits) * 100;

  // Check if we need to scale down (total > 100%)
  const total = completedPct + inProgressPct + plannedPct + suggestedPct + availablePct;
  const scale = total > 100 ? 100 / total : 1;

  return {
    completed: completedPct * scale,
    in_progress: inProgressPct * scale,
    planned: plannedPct * scale,
    suggested: suggestedPct * scale,
    available: availablePct * scale,
    total_percentage: Math.min(total, 100),
    is_overcommitted: total > 100,
    scale_factor: scale
  };
};
